import { useState } from "react";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { Link } from "react-router-dom";

import { PAGE_NAME_HOW_TO_FIX } from "../../../../constants/common_constants";
import {
  IInferenceFactorType,
  IReferenceInfoType,
} from "../types/inference_factor_type";
import { v4 as uuidv4 } from "uuid";
import { CategoryInfoType } from "../types/category_info_type";
import Short from "../../../../icons/Gauge_S.svg";
import Medium from "../../../../icons/Gauge_M.svg";
import Long from "../../../../icons/Gauge_L.svg";
import {
  WORKHOUR_SHORT,
  WORKHOUR_MEDIUM,
  WORKHOUR_LONG,
} from "../../../../constants/common_constants";

// アコーディオンメニューコンポーネントに渡すprops
interface InferenceFactorAccordionListsProps {
  inferenceFactor: IInferenceFactorType;
  children?: React.ReactNode;
  categoryInfo: CategoryInfoType;
}

/**
 * アコーディオンメニューを表示するコンポーネント(リスト取得対応版)
 * @param inferenceFactor - 推定要因項目
 * @param children - 子要素
 * @returns JSX.Element - アコーディオンメニューの表示
 */
export const InferenceFactorAccordionLists = ({
  inferenceFactor,
  children,
  categoryInfo,
}: InferenceFactorAccordionListsProps) => {
  // メニューが開いているかどうかを格納
  const [open, setOpen] = useState(false);

  // リストの開閉をトグル処理
  const toggleButton = () => {
    setOpen((prevState) => !prevState);
  };
  const handleJumpToAnotherTabLink = (reference: string, title: string) => {
    const keyId = uuidv4();
    const refKey = `reference_${keyId}`;
    //LocalStorageにマニュアルリンク, タイトル(参照情報のラベル)を保存する
    localStorage.setItem(refKey, reference);
    localStorage.setItem("title", title);
    //一時的に<a>タグを作成して別タブでリンクを開く
    const link = document.createElement("a");
    link.target = "_blank";
    link.href = `/${PAGE_NAME_HOW_TO_FIX}/${keyId}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //#の有無を判定して#が無いものには#を付与する関数
  const ensureHash = (colorCode: string): string => {
    return colorCode[0] === "#" ? colorCode : `#${colorCode}`;
  };

  //stateに値に入る前に初回レンダリングが発生するため、bg,textに値が入っていない場合の色を設定する必要がある
  const bg = categoryInfo?.backColor?.trim();
  const border = categoryInfo?.fontColor?.trim();
  const bgCode = bg ? ensureHash(bg) : "#ffffff";
  const borderCode = border ? ensureHash(border) : "#000000";

  //工数によって表示させるアイコンを変える関数
  const changeWorkloadImage = (workload: number) => {
    switch (workload) {
      case WORKHOUR_SHORT:
        return <img src={Short} alt="工数アイコン（小）" />;
      case WORKHOUR_MEDIUM:
        return <img src={Medium} alt="工数アイコン（中）" />;
      case WORKHOUR_LONG:
        return <img src={Long} alt="工数アイコン（大）" />;
      default:
        return "";
    }
  };

  // 参照情報(3階層目)をレンダリングする関数
  const renderReferences = () => {
    const referenceArray =
      inferenceFactor.issueFactor.repairFactor.referenceInfo;
    return referenceArray.map(
      (referenceInfo: IReferenceInfoType, index: number) => (
        <dd className="contents" key={referenceInfo.label}>
          <div
            style={{ ["--cat-border" as any]: borderCode }}
            className={`flex items-center
               ${
                 index - 1 !== referenceArray.length &&
                 "border-t border-[var(--cat-border)]"
               } 
             py-2 pl-16 pr-4`}
          >
            {referenceInfo.reference ? (
              referenceInfo.reference.includes("http") ? (
                // referenceに入っている文字列にhttpが入っていた場合(外部リンクの場合),
                // 青文字でlabelを返し別タブでそのURLを開くリンクをつける
                <Link
                  to={referenceInfo.reference}
                  target="_blank"
                  rel="noopener noreferrer"
                  // className="text-sub-blue line-clamp-2"
                  className="text-sub-blue"
                >
                  {referenceInfo.label}
                </Link>
              ) : (
                // referenceに文字列が入っていた場合(ページ遷移の場合),
                // 青文字でlabelをそのページに遷移するリンクをつける
                <button
                  // className="text-left text-sub-blue line-clamp-2"
                  className="text-left text-sub-blue"
                  onClick={() =>
                    handleJumpToAnotherTabLink(
                      referenceInfo.reference,
                      referenceInfo.label,
                    )
                  }
                >
                  {referenceInfo.label}
                </button>
              )
            ) : (
              //  referenceに何もない場合, 黒文字のままlabelを返す
              // <p className="line-clamp-2">{referenceInfo.label}</p>
              <p>{referenceInfo.label}</p>
            )}
          </div>
        </dd>
      ),
    );
  };

  // ラベル部分(1・2階層目)をレンダリングする関数
  const renderMenu = () => {
    // childrenの有無によって表示するラベルの取得方法分岐
    const label = children
      ? inferenceFactor.issueFactor.label // (label = 要因項目)
      : inferenceFactor.issueFactor.repairFactor.label; // (label = 解決方法)

    return (
      <div
        // className={`grow line-clamp-2 cursor-pointer`}
        className="flex items-center justify-between grow cursor-pointer"
        onClick={toggleButton}
      >
        <p className="flex-1 pr-2">{label}</p>
        <div className="pr-[6px]">
          {/* 工数によって表示させるアイコンを変更 */}
          {children
            ? changeWorkloadImage(
                inferenceFactor.issueFactor.repairFactor.workload,
              )
            : ""}
        </div>
      </div>
    );
  };

  // 各リスト部分をレンダリングする関数
  const renderList = () => {
    return (
      <div>
        <dt
          className={`flex items-center justify-between cursor-pointer py-2 ${
            children ? "pl-3" : "pl-8"
          } pr-3`}
        >
          <div className="pr-3 cursor-pointer" onClick={toggleButton}>
            {open ? <FaMinus /> : <FaPlus />}
          </div>
          {renderMenu()}
          {/* {children && (
            // 一番上のリストの場合のみ処理
            <p className="pl-2 text-end">
              {(inferenceFactor.issueFactor.probability * 100).toFixed(2)}%
            </p>
          )} */}
        </dt>
        {open && (children || renderReferences())}
      </div>
    );
  };

  return (
    <dl
      style={{
        ["--cat-bg" as any]: bgCode,
        ["--cat-border" as any]: borderCode,
      }}
      className={`bg-[var(--cat-bg)]  ${
        children
          ? "mb-4 rounded border-[3px] border-[var(--cat-border)]"
          : "border-t border-[var(--cat-border)]"
      }`}
    >
      {renderList()}
    </dl>
  );
};
